// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { availabilityService } from "../../../services/AvailabilityService";
import locationService from "../../../services/locationService";
import scenarioService from "../../../services/scenarioService";
import tenantService from "../../../services/tenantService";
import { usePermissions } from "../../../hooks/usePermissions";
import { extractPaginatedResponse } from "../../../utils/apiHelper";
import { TYPE_KEYS } from "../constants";

const DEFAULT_FILTERS = {
  // Empty until the real locations load; the backend resolves this value by
  // numeric id / slug / vhp_code, so we pass the location's numeric id.
  location: "",
  scenario: "all",
  source: "all",
  tenant: "all",
};

const VALID_TYPES = new Set(TYPE_KEYS);

/** Local-time YYYY-MM-DD (avoids the UTC shift of toISOString). */
function formatDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Range covering the displayed month ±1 for adjacent-month pre-loading. */
function monthRange(currentMonth) {
  const y = currentMonth.getFullYear();
  const m = currentMonth.getMonth();
  return {
    dateFrom: formatDateKey(new Date(y, m - 1, 1)),
    dateTo: formatDateKey(new Date(y, m + 2, 0)),
  };
}

/** Map a raw occupied-days API row onto the shape the UI components expect. */
function normalizeOccupied(row, index) {
  const date = (row.date || "").slice(0, 10);
  const scenario =
    row.scenario_id == null || row.scenario_id === "" ? "all" : row.scenario_id;
  return {
    // The endpoint has no stable per-row id; synthesize one for React keys.
    id: row.id ?? `${row.ref || row.source || "row"}-${date}-${index}`,
    date,
    type: VALID_TYPES.has(row.type) ? row.type : "event",
    title: row.title || "",
    org: row.org || "",
    source: row.source || "booking",
    location: row.location_id ?? row.location ?? null,
    scenario,
    time: row.time || "",
    status: row.status || "confirmed",
    ref: row.ref || "",
    tenant: row.tenant_id ?? row.tenant ?? null,
    seasNote: row.seas_note ?? row.seasNote ?? null,
  };
}

export function useAvailabilityView() {
  const { isSuperAdmin } = usePermissions();

  const [currentView, setCurrentView] = useState("calendar");
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activeTypes, setActiveTypes] = useState(() => new Set(TYPE_KEYS));
  const [detailDate, setDetailDate] = useState(null);
  const [panelClosing, setPanelClosing] = useState(false);
  const [viewStatus, setViewStatus] = useState("");

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real dropdown options fetched from the backend (replaces prototype slugs).
  const [locationOptions, setLocationOptions] = useState([]);
  const [scenarioOptions, setScenarioOptions] = useState([
    { value: "all", label: "Alle Szenarien" },
  ]);
  const [tenantOptions, setTenantOptions] = useState([
    { value: "all", label: "Alle Mandanten" },
  ]);

  const calendarRef = useRef(null);
  const listRef = useRef(null);

  // Load real tenants for the SuperAdmin-only selector. Other roles never see
  // it (and the /occupied endpoint ignores the tenant override for them).
  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let active = true;
    tenantService
      .getAllTenantsForFilter({ includeAll: true, allLabel: "Alle Mandanten" })
      .then((rows) => {
        if (!active) return;
        const opts = (Array.isArray(rows) ? rows : [])
          .filter((t) => t && t.id != null)
          // Keep the "Alle Mandanten" entry; otherwise only active tenants
          // (inactive / suspended / demo are excluded).
          .filter(
            (t) =>
              t.id === "all" ||
              String(t.status || "").toLowerCase() === "active",
          )
          .map((t) => ({ value: String(t.id), label: t.name || String(t.id) }));
        if (opts.length) setTenantOptions(opts);
      })
      .catch(() => {
        if (active)
          setTenantOptions([{ value: "all", label: "Alle Mandanten" }]);
      });
    return () => {
      active = false;
    };
  }, [isSuperAdmin]);

  // Load real locations once and default the filter to the first one. The
  // value is the location's numeric id, which the /occupied endpoint resolves
  // reliably (slug is unseeded, so the old "hardenberg" default 404'd).
  useEffect(() => {
    let active = true;
    locationService
      .getLocations({ limit: 500 })
      .then((response) => {
        if (!active) return;
        const { data: rows } = extractPaginatedResponse(response);
        const opts = (Array.isArray(rows) ? rows : [])
          .filter((l) => l && l.id != null)
          .map((l) => ({
            value: String(l.id),
            label: l.name || l.vhp_code || `Standort ${l.id}`,
          }))
          .sort((a, b) => Number(a.value) - Number(b.value));
        setLocationOptions(opts);
        setFilters((prev) =>
          prev.location || opts.length === 0
            ? prev
            : { ...prev, location: opts[0].value },
        );
      })
      .catch(() => {
        if (active) setLocationOptions([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // Load scenarios for the active location and reset the scenario filter to
  // "all". Scenario ids are integers; the endpoint rejects non-numeric ids.
  useEffect(() => {
    if (!filters.location) return undefined;
    let active = true;
    scenarioService
      .getScenarios({ location_id: filters.location, limit: 500 })
      .then((response) => {
        if (!active) return;
        const { data: rows } = extractPaginatedResponse(response);
        const opts = (Array.isArray(rows) ? rows : [])
          .filter((s) => s && s.id != null)
          .map((s) => ({
            value: String(s.id),
            label: s.name || s.scenario_name || `Szenario ${s.id}`,
          }))
          .sort((a, b) => Number(a.value) - Number(b.value));
        setScenarioOptions([
          { value: "all", label: "Alle Szenarien" },
          ...opts,
        ]);
      })
      .catch(() => {
        if (active)
          setScenarioOptions([{ value: "all", label: "Alle Szenarien" }]);
      });
    return () => {
      active = false;
    };
  }, [filters.location]);

  // Fetch occupied days whenever the server-side query inputs change. The
  // location/scenario/source filters are applied server-side; the displayed
  // month (±1) defines the date range. Type chips stay client-side below.
  useEffect(() => {
    // The endpoint requires a concrete location; skip until one is selected.
    if (!filters.location || filters.location === "all") {
      setData([]);
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    const { dateFrom, dateTo } = monthRange(currentMonth);

    setIsLoading(true);
    setError(null);

    availabilityService
      .getOccupied(
        {
          locationId: filters.location,
          scenarioId: filters.scenario,
          dateFrom,
          dateTo,
          source: filters.source,
          // Honoured server-side only for super-admins.
          tenantId: isSuperAdmin ? filters.tenant : undefined,
        },
        { signal: controller.signal },
      )
      .then((response) => {
        if (!active) return;
        const { data: rows } = extractPaginatedResponse(response);
        setData(
          (Array.isArray(rows) ? rows : []).map((row, i) =>
            normalizeOccupied(row, i),
          ),
        );
      })
      .catch((err) => {
        if (!active || controller.signal.aborted) return;
        setData([]);
        setError(err);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    filters.location,
    filters.scenario,
    filters.source,
    filters.tenant,
    isSuperAdmin,
    currentMonth,
  ]);

  // Client-side narrowing: type chips always, plus a tolerant pass over the
  // server filters in case the endpoint returns a broader set.
  const filteredData = useMemo(() => {
    return data.filter((d) => {
      if (!activeTypes.has(d.type)) return false;
      if (
        filters.location !== "all" &&
        d.location != null &&
        d.location !== filters.location
      )
        return false;
      if (
        filters.scenario !== "all" &&
        d.scenario !== filters.scenario &&
        d.scenario !== "all"
      )
        return false;
      if (filters.source !== "all" && d.source !== filters.source) return false;
      if (
        filters.tenant !== "all" &&
        d.tenant != null &&
        d.tenant !== filters.tenant
      )
        return false;
      return true;
    });
  }, [data, filters, activeTypes]);

  const stats = useMemo(() => {
    const counts = { event: 0, delivery: 0, construction: 0, infra: 0 };
    filteredData.forEach((d) => {
      if (counts[d.type] !== undefined) counts[d.type]++;
    });
    const total = filteredData.length;
    const seas = filteredData.filter((d) => d.source === "seas").length;
    return { counts, total, seas };
  }, [filteredData]);

  const detailItems = useMemo(() => {
    if (!detailDate) return [];
    return filteredData.filter((d) => d.date === detailDate);
  }, [detailDate, filteredData]);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      // A scenario id is only valid for its location, so clear it when the
      // location changes (the scenario list reloads for the new location).
      if (key === "location") next.scenario = "all";
      return next;
    });
  }, []);

  const toggleType = useCallback((type) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size === 1) return prev;
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const changeMonth = useCallback((dir) => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + dir, 1),
    );
  }, []);

  const openDetail = useCallback(
    (dateStr) => {
      const hasItems = filteredData.some((d) => d.date === dateStr);
      if (hasItems) {
        setPanelClosing(false);
        setDetailDate(dateStr);
      }
    },
    [filteredData],
  );

  const closeDetail = useCallback(() => {
    setPanelClosing(true);
    setTimeout(() => {
      setDetailDate(null);
      setPanelClosing(false);
    }, 200);
  }, []);

  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
    setViewStatus(
      view === "calendar" ? "Kalenderansicht geladen" : "Listenansicht geladen",
    );
  }, []);

  useEffect(() => {
    if (!viewStatus) return;
    const target = currentView === "calendar" ? calendarRef : listRef;
    const el = target.current;
    if (!el) return;
    el.setAttribute("tabindex", "-1");
    el.focus({ preventScroll: true });
    const t = setTimeout(() => el.removeAttribute("tabindex"), 100);
    return () => clearTimeout(t);
  }, [currentView, viewStatus]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && detailDate) closeDetail();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [detailDate, closeDetail]);

  return {
    currentView,
    setCurrentView: handleViewChange,
    currentMonth,
    changeMonth,
    filters,
    updateFilter,
    locationOptions,
    scenarioOptions,
    tenantOptions,
    isSuperAdmin,
    activeTypes,
    toggleType,
    filteredData,
    stats,
    detailDate,
    detailItems,
    panelClosing,
    openDetail,
    closeDetail,
    viewStatus,
    isLoading,
    error,
    calendarRef,
    listRef,
  };
}
